import React from 'react';
import { Controller, Control, useFieldArray } from 'react-hook-form';
import { Grid, TextField, Typography, Box } from '@mui/material';
import { LactateFormValues } from '../../formModel';
import { Modality } from '../../types';
import { unitsForModality } from '../../constants';

const BOUNDARY_LABEL: Record<string, string> = {
  Z1_Z2: 'Z1 / Z2 boundary',
  Z2_Z3: 'Z2 / Z3 boundary',
  Z3_Z4: 'Z3 / Z4 boundary',
  Z4_Z5: 'Z4 / Z5 boundary',
  Z5_Z6_7: 'Z5 / Z6-7 boundary',
};

interface Props {
  control: Control<LactateFormValues>;
  modality: Modality;
  errors: Record<string, string>;
}

export const ZoneSection: React.FC<Props> = ({ control, modality, errors }) => {
  const { intensityLabel, intensityUnit } = unitsForModality(modality);
  const { fields } = useFieldArray({ control, name: 'zone_boundaries' });

  return (
    <>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Training zone boundaries
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {fields.map((row, i) => {
          const boundary = (row as unknown as { boundary: string }).boundary;
          // The top (Z5/Z6-7) boundary has no HR: Z6/7 is a power zone and the
          // report renders Z5 as an open-ended "> b4" band, so HR is not collected.
          const hasHr = boundary !== 'Z5_Z6_7';
          return (
            <Grid container spacing={1.5} alignItems="center" key={row.id}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {BOUNDARY_LABEL[boundary]}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Controller
                  name={`zone_boundaries.${i}.intensity` as const}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={`${intensityLabel} (${intensityUnit})`}
                      fullWidth
                      size="small"
                      error={Boolean(errors[`zone_boundaries.${i}.intensity`])}
                      helperText={errors[`zone_boundaries.${i}.intensity`]}
                    />
                  )}
                />
              </Grid>
              {hasHr && (
                <Grid item xs={6} sm={4}>
                  <Controller
                    name={`zone_boundaries.${i}.hr` as const}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="HR (bpm)"
                        fullWidth
                        size="small"
                        error={Boolean(errors[`zone_boundaries.${i}.hr`])}
                        helperText={errors[`zone_boundaries.${i}.hr`]}
                      />
                    )}
                  />
                </Grid>
              )}
            </Grid>
          );
        })}
      </Box>
    </>
  );
};
